import React from 'react';
import styled from 'styled-components';

import { Command, State, tabActions } from '@store/index';
import { Label } from '@common/Label';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '@common/Select';
import { Expression } from '@common/Expression';
import { NodeField, NodeFieldType, NodeInput, NodeInputType, NodeState } from '@components/Flow/Nodes';
import { BasicInput } from '@common/Expression/Basic';
import { NodeFixedInput } from './Fixed';
import { useNodeData } from '@common/hooks/useNodeData';
import { getValidation } from '@common/Node/utils';
import { Alert, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { NodeMessageArray } from './NodeMessageArray';
import { SecretInput } from '@common/NodeAuth/SecretInput';
import { useTheme } from '../../../../styles';
import { ErrorMessage } from '@common/Autocomplete';
import { customAmdBehaviour, customStreamBehaviour } from '@components/Flow/Nodes/CallControl/Dial/Dial';

const Container = styled.div<{ hoverDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &:hover {
    .switch {
      display: ${({ hoverDisabled }) => (hoverDisabled ? 'hidden' : 'block')};
    }
  }
`;

interface Props {
  field: NodeField;
  command: Command;
  options?: { label: string; value: string }[];
  minRows?: number;
  maxRows?: number;
  allowToolArgs?: boolean;
  override?: {
    title: string;
    tooltip?: string;
    value?: NodeInput<string> | string;
    update: (fieldName: 'value' | 'type', value: string | boolean) => void;
    optionType?: boolean;
    placeholder?: string;
  };
}

export const NodeInputExpression: React.FC<Props> = ({
  field,
  command,
  options,
  minRows,
  maxRows,
  allowToolArgs,
  override,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { id, label, placeholder, description } = field;
  const { getOptions, dataEdges } = useNodeData(command?.node?.id);
  const { workflow_id } = useParams();

  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);

  const state = command.state;
  const isOption = field.type === NodeFieldType.Option || field.type === NodeFieldType.OptionExpression;
  const isFixedInput =
    field.type === NodeFieldType.Input ||
    field.type === NodeFieldType.Option ||
    field.type === NodeFieldType.Toggle ||
    field.type === NodeFieldType.ModelWithTools;

  const dataOptions = getOptions(command.node.id, allowToolArgs);
  const hasOptions = Object.keys(dataOptions || {}).length > 0;

  const toolWarningFields = ['bucket_search', 'api_search', 'custom_output'];
  const showToolWarning = toolWarningFields.includes(field.id);

  // custom option can be used to change the input switcher type
  const getCustomOption = () => {
    if (field.type === NodeFieldType.NumberExpression) return { label: 'Number', value: NodeInputType.Number };
    if (id === 'messages') return { label: 'Fixed', value: NodeInputType.MessageExpression };
    return undefined;
  };

  const updateField = (fieldName: 'value' | 'type' | undefined, value: string | boolean) => {
    if (typeof override?.update === 'function' && fieldName) return override.update(fieldName, value);

    const nodeId = command.node.id;
    const fieldId = id;

    if (field.validation) value = getValidation(field.validation)(value as string);
    if (typeof value === 'string') customNodeBehaviour(value);

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId, fieldName, value }));
    dispatch(tabActions.setSaved(false));
  };

  /**
   * Used to handle side effects for updating certain state.
   */

  const customNodeBehaviour = (value: string | string[]) => {
    customStreamBehaviour(value, field, command, dispatch);
    customAmdBehaviour(value, field, command, dispatch);
  };

  /**
   * A fixed input is a field that cannot be set as an expression.
   */

  if (isFixedInput) {
    return <NodeFixedInput key={field.id} field={field} command={command} override={override} />;
  }

  try {
    const { value, type } = override?.value || state[field.id as keyof NodeState];

    const error = nodeErrors?.find(
      (nodeError) =>
        nodeError.id === command.node.id &&
        nodeError?.workflowId === workflow_id &&
        nodeError?.fields?.find((nodeField) => nodeField.id === field.id)
    );
    const errorField = error ? error?.fields?.find((nodeField) => nodeField.id === field.id) : undefined;
    const errorFieldMessage = errorField ? errorField.message : undefined;

    const isInputSwitchDisabled = field.id === 'call_control_id';

    return (
      <Container hoverDisabled={isInputSwitchDisabled}>
        <Label
          title={override?.title || label}
          tooltip={override?.tooltip || description}
          optionAlignment={override?.optionType || isOption}
          customOption={getCustomOption()}
          alignment={type}
          setAlignment={(newValue) => newValue && updateField('type', newValue)}
        />
        {(type === NodeInputType.Text || type === NodeInputType.Number) && (
          <BasicInput
            key={`${command.node.id}-${field.id}`}
            keyProp={`${command.node.id}-${field.id}`}
            value={value}
            setValue={(newValue: string) => updateField('value', newValue)}
            placeholder={override?.placeholder || placeholder || ''}
            minRows={minRows ? minRows : undefined}
            maxRows={maxRows ? maxRows : undefined}
            numberOnly={type === NodeInputType.Number}
            maxNumber={type === NodeInputType.Number ? field?.maxNumber : undefined}
            minNumber={type === NodeInputType.Number ? field?.minNumber : undefined}
            error={errorFieldMessage}
          />
        )}
        {field.type === NodeFieldType.Secret && (
          <SecretInput
            key={field.id}
            field={field}
            command={command}
            value={state?.[field.id] || ''}
            updateField={(value: string) => updateField(undefined, value)}
          />
        )}
        {type === NodeInputType.Option && (
          <Select
            key={field.id}
            value={value}
            setValue={(newValue) => updateField('value', newValue)}
            placeholder={override?.placeholder || placeholder}
            options={
              options ||
              (field?.options as { label: string; value: string }[]) || [
                { label: 'true', value: 'true' },
                { label: 'false', value: 'false' },
              ]
            }
          />
        )}
        {errorFieldMessage && type === NodeInputType.Option && (
          <Stack direction='row' spacing={1}>
            <ErrorMessage>{errorFieldMessage}</ErrorMessage>
          </Stack>
        )}
        {type === NodeInputType.Expression && (
          <Expression
            key={`${command.node.id}-${field.id}`}
            keyProp={`${command.node.id}-${field.id}`}
            value={value}
            dataEdges={dataEdges}
            title={label}
            description={description}
            setValue={(newValue: string) => updateField('value', newValue)}
            options={dataOptions}
            placeholder={override?.placeholder || placeholder || ''}
            minRows={minRows ? minRows : undefined}
            maxRows={maxRows ? maxRows : undefined}
            error={errorFieldMessage}
          />
        )}
        {type === NodeInputType.MessageExpression && <NodeMessageArray field={field} command={command} />}
        {type === NodeInputType.Expression && (value.includes('{') || value.includes('}')) && !hasOptions && (
          <Alert severity='warning'>
            <div>
              This node does not have any data connected. Connect the <strong>Data</strong> anchor from a previous node
              into the <strong>Data</strong> anchor for this node to access dynamic variables.{' '}
              <a
                style={{ color: theme.primary, textDecoration: 'underscore', marginTop: '0.5rem' }}
                href={`/documentation/dynamic_variables`}
                target='_blank'
                rel='noreferrer'
              >
                More Info.
              </a>
            </div>
            {showToolWarning && (
              <div style={{ marginTop: '12px' }}>
                You can also add data to this node by creating arguments in the <strong>Arguments</strong> tab.
              </div>
            )}
          </Alert>
        )}
      </Container>
    );
  } catch {
    return null;
  }
};
